// Which addresses the session recorder is allowed to run on — decided here, in
// code, and never in the measurement tool's own dashboard.
//
// The reason it is code: the cabinet and the console render customers' end-user
// document fields, and a dashboard setting is one click and no review away from
// including them. A list in a file is a diff, a test and a build. The guard test
// beside this module walks the app's whole route tree and fails on any route
// that neither list names, so a route ADDED under the cabinet is covered by the
// rule the day it exists rather than the day somebody remembers it.
//
// Browser-safe: no framework import, no Node built-in.
//
// A translated page (`/es/pricing`) is the same page as its English original
// in another language, so it is decided by the English path its address
// carries: the rule is written once, in English paths, and holds for every
// language.

import { splitLocale } from "../i18n/locales.ts";

/**
 * Prefixes that never carry a recording, whatever else matches.
 *
 * `/app`, `/admin` and `/account` are the signed-in surfaces. The
 * authentication pages are here for a different reason: nothing on them is a
 * document, but a recording of a person typing into a password field is exactly
 * the artefact nobody should have to trust masking for.
 */
export const REPLAY_DENIED_PREFIXES: readonly string[] = [
  "/app",
  "/admin",
  "/account",
  "/login",
  "/register",
  "/verify",
  "/forgot-password",
  "/reset-password",
  "/logout",
  "/auth",
  "/api",
  "/error-probe",
];

/**
 * The public surfaces a recording may be made on: the marketing site and the
 * documentation.
 *
 * `/` is included and carries the landing demo, which accepts a real document
 * from a visitor. That subtree is BLOCKED in the markup rather than
 * route-excluded here — see `REPLAY_BLOCK_CLASS` — because the rest of the page
 * is where the reason for recording it at all lies.
 */
export const REPLAY_ALLOWED_PREFIXES: readonly string[] = [
  "/",
  "/docs",
  "/about",
  "/contact",
  "/pricing",
  "/passport-ocr",
  "/document-field-explorer",
  "/mrz-checker",
  "/mrz-generator",
  // The written marketing pages beside the two free tools: the price
  // comparison, the no-account landing, the MCP page and the MRZ format
  // explainer. Public prose with no form and no document on them, so they are
  // the same case as /pricing above.
  "/compare",
  "/free-passport-ocr-api",
  "/mcp",
  "/mrz-formats",
  // The articles: public prose again, the same case as the pages above.
  "/blog",
  // The Spanish page for hotels' guest registers, which exists only in
  // Spanish: public prose with no form, the same case again.
  "/registro-de-viajeros-hoteles",
  "/status",
  "/terms",
  "/privacy",
  "/data-processing",
];

/**
 * The class the recorder's underlying library replaces an element with a blank
 * placeholder for, children and attributes and all.
 *
 * It is that library's own default rather than a name of ours, and that is
 * deliberate: the measurement tool passes no block class of its own, so the
 * default is what is in force, and a name we invented would only work if a
 * server-side setting named it. This one needs nothing but the markup.
 */
export const REPLAY_BLOCK_CLASS = "rr-block";

function underPrefix(pathname: string, prefix: string): boolean {
  if (prefix === "/") return pathname === "/";
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Whether a recording may be made on this address.
 *
 * Deny wins, and an address that matches nothing is denied: a route nobody
 * classified is a route nobody thought about, and the safe answer to that is
 * not to record it.
 */
export function replayAllowed(pathname: string): boolean {
  // Not even a path is the strongest case of "nobody classified this".
  if (typeof pathname !== "string" || pathname === "") return false;
  const path = splitLocale(pathname).path;
  if (REPLAY_DENIED_PREFIXES.some((prefix) => underPrefix(path, prefix))) return false;
  if (REPLAY_ALLOWED_PREFIXES.some((prefix) => underPrefix(path, prefix))) return true;
  // The generated country pages, which are ~200 addresses from one template and
  // the largest part of the public surface.
  return /^\/[a-z0-9-]+-passport-ocr$/.test(path);
}
